/**
 * Consignment API Client
 * Package-based rental with consignment (bizomány) support
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type ConsignmentStatus = 'ACTIVE' | 'SETTLED' | 'PARTIALLY_SETTLED';

export interface ConsignmentItem {
  id: string;
  consignmentId: string;
  productId: string;
  productName: string;
  productSku: string;
  quantityOut: number;
  quantityReturned: number;
  quantityCharged: number;
  unitPrice: number;
  chargedAmount: number;
  settledAt?: string;
}

export interface Consignment {
  id: string;
  rentalId: string;
  rentalCode: string;
  partnerId: string;
  partnerName: string;
  status: ConsignmentStatus;
  deliveryNoteNumber: string;
  items: ConsignmentItem[];
  totalValue: number;
  settledValue: number;
  outstandingValue: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Flat consignment row a GET /consignments/summary items-ében.
 * A BE ConsignmentEntity (@kgc/packages consignment.service) JSON-serializált
 * párja — a Date mezők ISO-stringként érkeznek.
 */
export interface ConsignmentSummaryItem {
  id: string;
  tenantId: string;
  rentalId: string;
  productId: string;
  quantityOut: number;
  unitPriceAtOut: number;
  outDate: string;
  deliveryNoteRef: string | null;
  quantityReturned: number | null;
  quantityCharged: number | null;
  totalCharge: number | null;
  settledAt: string | null;
  settledBy: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConsignmentSummary {
  totalValue: number;
  partnerCount: number;
  oldestDays: number;
  items: ConsignmentSummaryItem[];
}

export interface SettleConsignmentInput {
  settlements: Array<{
    consignmentId: string;
    quantityReturned: number;
  }>;
}

export interface SettleConsignmentResponse {
  data: Consignment[];
}

export interface CreateConsignmentInput {
  items: Array<{
    productId: string;
    quantity: number;
    unitPrice: number;
  }>;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Create consignment for a rental (dispatch items on delivery note)
 */
export async function createConsignment(
  rentalId: string,
  data: CreateConsignmentInput
): Promise<{ data: Consignment[] }> {
  return api.post<{ data: Consignment[] }>(`/rentals/${rentalId}/consignments`, data);
}

/**
 * Get consignments for a specific rental
 */
export async function getConsignmentsForRental(rentalId: string): Promise<{ data: Consignment[] }> {
  return api.get<{ data: Consignment[] }>(`/rentals/${rentalId}/consignments`);
}

/**
 * Settle consignment items (record returned quantities)
 */
export async function settleConsignment(
  rentalId: string,
  data: SettleConsignmentInput
): Promise<SettleConsignmentResponse> {
  return api.post<SettleConsignmentResponse>(`/rentals/${rentalId}/consignments/settle`, data);
}

/**
 * Get consignment summary (dashboard widget data)
 */
export async function getConsignmentSummary(): Promise<{ data: ConsignmentSummary }> {
  return api.get<{ data: ConsignmentSummary }>('/consignments/summary');
}

/**
 * Get delivery note PDF URL for a consignment batch
 */
export function getDeliveryNotePdfUrl(rentalId: string, deliveryNoteRef: string): string {
  return `/rentals/${rentalId}/consignments/delivery-note/${encodeURIComponent(deliveryNoteRef)}/pdf`;
}
